import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '@/components/AuthProvider';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { MerchantApplicationService } from '@/features/merchant-application/services/merchantApplicationService';
import { EnhancedMerchantPipeline } from '@/features/merchant-application/components/EnhancedMerchantPipeline';
import { MerchantApplicationsList } from '@/features/merchant-application/components/MerchantApplicationsList';
import { NewApplicationDialog } from '@/features/merchant-application/components/NewApplicationDialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle, List, Plus, X, ArrowRight, Download, LayoutGrid, Users, Clock, CheckCircle2, Search, Filter } from 'lucide-react';
import { MerchantApplicationErrorBoundary } from '@/features/merchant-application/components/MerchantApplicationErrorBoundary';
import { cn } from '@/lib/utils';
import { startOfToday } from 'date-fns';

export default function MerchantApplicationsManagement() {
  const { isSuperAdmin, loading } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'pipeline');
  const [showNewApplicationDialog, setShowNewApplicationDialog] = useState(false);
  const [pipelineData, setPipelineData] = useState<{
    bulkActionMode: boolean;
    setBulkActionMode: (mode: boolean) => void;
    selectedApplicationsCount: number;
    applicationsCount: number;
    showAddColumn: boolean;
    setShowAddColumn: (show: boolean) => void;
    handleExportSelected: () => void;
    selectedApplications: Set<string>;
    setSelectedApplications: (apps: Set<string>) => void;
  } | null>(null);

  // Fetch all applications for statistics
  const { data: applications = [] } = useQuery({
    queryKey: ['merchantApplications'],
    queryFn: () => MerchantApplicationService.getAllApplications(),
    enabled: isSuperAdmin,
  });

  // Calculate statistics
  const statistics = useMemo(() => {
    const today = startOfToday();

    const approvedToday = applications.filter(app => {
      const createdAt = app.createdAt instanceof Date ? app.createdAt : app.createdAt?.toDate?.();
      return app.pipelineStatus === 'approved' &&
             createdAt &&
             createdAt >= today;
    }).length;

    const pendingReview = applications.filter(app =>
      ['phone', 'offer', 'underwriting', 'documents'].includes(app.pipelineStatus || '')
    ).length;

    return { approvedToday, pendingReview };
  }, [applications]);

  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab && ['pipeline', 'list'].includes(tab)) {
      setActiveTab(tab);
    }
  }, [searchParams]);

  const handleTabChange = (value: string) => {
    setActiveTab(value);
    setSearchParams({ tab: value });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (!isSuperAdmin) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            You don't have permission to access this page. Only super admins can manage merchant applications.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="space-y-6 -mx-6 -mr-6 px-6 pr-0">
      {/* Modern Header */}
      <div className="bg-white rounded-l-2xl shadow-sm border border-gray-200/50 border-r-0">
        <div className="px-6 py-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-2.5 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl shadow-lg shadow-blue-500/20">
                <Users className="h-6 w-6 text-white" />
              </div>
              <div>
                <p className="text-gray-500 text-sm">Track and manage your merchant application pipeline</p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              {/* Stats */}
              <div className="hidden md:flex items-center gap-6 mr-6">
                <div className="flex items-center gap-2">
                  <div className="p-1.5 bg-green-100 rounded-lg">
                    <CheckCircle2 className="h-4 w-4 text-green-600" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Approved Today</p>
                    <p className="text-sm font-semibold text-gray-900">{statistics.approvedToday}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="p-1.5 bg-amber-100 rounded-lg">
                    <Clock className="h-4 w-4 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Pending Review</p>
                    <p className="text-sm font-semibold text-gray-900">{statistics.pendingReview}</p>
                  </div>
                </div>
              </div>
              
              <Button 
                onClick={() => setShowNewApplicationDialog(true)}
                className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white shadow-lg shadow-blue-600/20 transition-all duration-200 hover:shadow-xl hover:shadow-blue-600/30"
              >
                <Plus className="h-4 w-4 mr-2" />
                New Application
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* View Toggle and Controls */}
      <div className="bg-white rounded-l-2xl shadow-sm border border-gray-200/50 border-r-0 p-4">
        <div className="flex items-center justify-between">
            {/* Modern Toggle Switch */}
            <div className="flex items-center gap-4">
              <div className="relative p-1 bg-gray-100 rounded-xl">
                <div 
                  className={cn(
                    "absolute inset-y-1 w-1/2 bg-white rounded-lg shadow-sm transition-all duration-300",
                    activeTab === 'list' && "translate-x-full"
                  )}
                />
                <div className="relative flex">
                  <button
                    onClick={() => handleTabChange('pipeline')}
                    className={cn(
                      "flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200",
                      activeTab === 'pipeline' 
                        ? "text-blue-600" 
                        : "text-gray-500 hover:text-gray-700"
                    )}
                  >
                    <LayoutGrid className="h-4 w-4" />
                    Pipeline View
                  </button>
                  <button
                    onClick={() => handleTabChange('list')}
                    className={cn(
                      "flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200",
                      activeTab === 'list' 
                        ? "text-blue-600" 
                        : "text-gray-500 hover:text-gray-700"
                    )}
                  >
                    <List className="h-4 w-4" />
                    List View
                  </button>
                </div>
              </div>

              {/* Search Bar */}
              <div className="relative flex-1 max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search applications..."
                  className="w-full pl-10 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
                />
              </div>
            </div>
            
            {/* Action Controls */}
            <div className="flex items-center gap-2">
              {activeTab === 'pipeline' && pipelineData && (
                <>
                  {pipelineData.bulkActionMode ? (
                    <>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          pipelineData.setBulkActionMode(false);
                          pipelineData.setSelectedApplications(new Set());
                        }}
                        className="text-gray-600 hover:text-gray-900"
                      >
                        <X className="h-4 w-4 mr-1.5" />
                        Cancel
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={pipelineData.selectedApplicationsCount === 0}
                        onClick={() => console.log('Bulk move clicked')}
                        className="border-gray-200 hover:bg-gray-50"
                      >
                        <ArrowRight className="h-4 w-4 mr-1.5" />
                        Move ({pipelineData.selectedApplicationsCount})
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={pipelineData.selectedApplicationsCount === 0}
                        onClick={pipelineData.handleExportSelected}
                        className="border-gray-200 hover:bg-gray-50"
                      >
                        <Download className="h-4 w-4 mr-1.5" />
                        Export
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => pipelineData.setBulkActionMode(true)}
                        className="text-gray-600 hover:text-gray-900"
                      >
                        <CheckCircle2 className="h-4 w-4 mr-1.5" />
                        Select
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-gray-600 hover:text-gray-900"
                      >
                        <Filter className="h-4 w-4 mr-1.5" />
                        Filter
                      </Button>
                      <div className="h-6 w-px bg-gray-200 mx-1" />
                      <Badge 
                        variant="secondary" 
                        className="bg-gray-100 text-gray-700 border-0 font-medium px-3"
                      >
                        {pipelineData.applicationsCount} Applications
                      </Badge>
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => pipelineData.setShowAddColumn(true)}
                        className="border-gray-200 hover:bg-gray-50 ml-2"
                      >
                        <Plus className="h-4 w-4 mr-1.5" />
                        Add Stage
                      </Button>
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

      {/* Content Area */}
      <div className={cn(
        "bg-white rounded-l-2xl shadow-sm border border-gray-200/50 border-r-0",
        activeTab === 'pipeline' ? "p-0 w-full" : "p-6"
      )}>
        {activeTab === 'pipeline' ? (
          <MerchantApplicationErrorBoundary level="feature">
            <EnhancedMerchantPipeline
              hideHeader={true}
              onPipelineDataChange={setPipelineData}
            />
          </MerchantApplicationErrorBoundary>
        ) : (
          <MerchantApplicationErrorBoundary level="feature">
            <MerchantApplicationsList />
          </MerchantApplicationErrorBoundary>
        )}
      </div>

      {/* New Application Dialog */}
      <NewApplicationDialog 
        open={showNewApplicationDialog} 
        onOpenChange={setShowNewApplicationDialog} 
      />
    </div>
  );
}
